
  import { defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { SeoObject } from '@/store/modules/SeoModule';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import MonacoEditor from '@guolao/vue-monaco-editor';

  export default defineComponent({
    name: 'seo-configuration',
    components: {
      ErrorMessage,
      Field,
      Form,
      MonacoEditor,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const seoNoIndexEn = ref(false);
      const seoNoFollowEn = ref(false);
      const seoNoIndexAr = ref(false);
      const seoNoFollowAr = ref(false);
      const seoSchemaEnJson = ref('');
      const seoSchemaArJson = ref('');
      const seoDataEn = ref<SeoObject>({
        seoTitle: '',
        seoDescription: '',
        seoKeywords: '',
        seoSlug: '',
        seoCanonicalUrl: '',
        seoImageAlt: '',
        seoSchema: null,
        seoNoIndex: false,
        seoNoFollow: false,
        seoCustomMeta: null,
        source: '',
      });

      const seoDataAr = ref<SeoObject>({
        seoTitle: '',
        seoDescription: '',
        seoKeywords: '',
        seoSlug: '',
        seoCanonicalUrl: '',
        seoImageAlt: '',
        seoSchema: null,
        seoNoIndex: false,
        seoNoFollow: false,
        seoCustomMeta: null,
        source: '',
      });

      const loading = ref(true);

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('seoConfiguration'), [
          translate('seo'),
        ]);
        fetchSeoData();
      });

      const fetchSeoData = async () => {
        loading.value = true;
        try {
          // Fetch English SEO Data
          const responseEn = await store.dispatch(
            Actions.GET_SEO_CONFIGURATION,
            {
              entityType: route.params.entityType,
              entityId: route.params.entityId,
              lang: 'en',
            }
          );
          seoDataEn.value = responseEn.data.data;

          seoNoIndexEn.value = seoDataEn.value.seoNoIndex;
          seoNoFollowEn.value = seoDataEn.value.seoNoFollow;

          seoSchemaEnJson.value = seoDataEn.value.seoSchema
            ? JSON.stringify(seoDataEn.value.seoSchema, null, 2)
            : '';

          if (seoDataEn.value.source == 'auto') {
            delete responseEn.data.data.source;
            await store.dispatch(Actions.CREATE_SEO_CONFIGURATION, {
              data: {
                ...responseEn.data.data,
                entityType: route.params.entityType,
                entityId: Number(route.params.entityId),
                lang: 'en',
                isActive: true,
              },
            });
          }

          // Fetch Arabic SEO Data
          const responseAr = await store.dispatch(
            Actions.GET_SEO_CONFIGURATION,
            {
              entityType: route.params.entityType,
              entityId: route.params.entityId,
              lang: 'ar',
            }
          );
          seoDataAr.value = responseAr.data.data;

          console.log(seoDataAr.value.seoKeywords);

          seoNoIndexAr.value = seoDataAr.value.seoNoIndex;
          seoNoFollowAr.value = seoDataAr.value.seoNoFollow;

          seoSchemaArJson.value = seoDataAr.value.seoSchema
            ? JSON.stringify(seoDataAr.value.seoSchema, null, 2)
            : '';

          if (seoDataAr.value.source == 'auto') {
            delete responseAr.data.data.source;
            await store.dispatch(Actions.CREATE_SEO_CONFIGURATION, {
              data: {
                ...responseAr.data.data,
                entityType: route.params.entityType,
                entityId: Number(route.params.entityId),
                lang: 'ar',
                isActive: true,
              },
            });
          }
        } catch (error) {
          console.error('Error fetching SEO data:', error);
        } finally {
          loading.value = false;
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );
        }
      };

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const validationSchema = Yup.object().shape({
        seoTitleEn: Yup.string()
          .required(() => translate('EN_NAME_IS_REQUIRED_FIELD'))
          .label('English Name'),
        seoTitleAr: Yup.string()
          .required(() => translate('AR_NAME_IS_REQUIRED_FIELD'))
          .label('Arabic Name'),
      });

      const onSubmitBoth = async (values) => {
        if (submitButton.value) {
          submitButton.value.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        console.log(values);

        const payloadEn = {
          entityType: route.params.entityType,
          entityId: route.params.entityId,
          lang: 'en',
          data: {
            lang: 'en',
            seoTitle: values.seoTitleEn,
            seoDescription: values.seoDescriptionEn,
            seoKeywords: values.seoKeywordsEn,
            seoSlug: values.seoSlugEn,
            seoCanonicalUrl: values.seoCanonicalUrl,
            seoImageAlt: values.seoImageAlt,
            seoNoIndex: seoNoIndexEn.value,
            seoNoFollow: seoNoFollowEn.value,
            seoSchema: seoSchemaEnJson.value
              ? JSON.parse(seoSchemaEnJson.value)
              : null,
          },
        };

        const payloadAr = {
          entityType: route.params.entityType,
          entityId: route.params.entityId,
          lang: 'ar',
          data: {
            lang: 'ar',
            seoTitle: values.seoTitleAr,
            seoDescription: values.seoDescriptionAr,
            seoKeywords: values.seoKeywordsAr,
            seoSlug: values.seoSlugAr,
            seoCanonicalUrl: values.seoCanonicalUrl,
            seoImageAlt: values.seoImageAlt,
            seoNoIndex: seoNoIndexAr.value,
            seoNoFollow: seoNoFollowAr.value,
            seoSchema: seoSchemaArJson.value
              ? JSON.parse(seoSchemaArJson.value)
              : null,
          },
        };

        await Promise.all([
          store.dispatch(Actions.UPDATE_SEO_CONFIGURATION, payloadEn),
          store.dispatch(Actions.UPDATE_SEO_CONFIGURATION, payloadAr),
        ]);

        submitButton.value?.removeAttribute('data-kt-indicator');
        submitButton.value!.disabled = false;

        Swal.fire({
          text: translate('SUCCESSFULLY_UPDATED_SEO_CONFIGURATION'),
          icon: 'success',
          buttonsStyling: false,
          confirmButtonText: translate('ok'),
          customClass: {
            confirmButton: 'btn btn-light-primary',
          },
        }).then(() => {
          if (route.params.entityType === 'auctions') {
            router.push({ name: 'auctions-listing' });
          } else if (route.params.entityType === 'blogs') {
            router.push({ name: 'blogs-listing' });
          } else if (route.params.entityType === 'service') {
            router.push({ name: 'home-services-listing' });
          } else if (route.params.entityType === 'auctions-properties') {
            router.push({ name: 'auctions-properties-listing' });
          } else {
            router.push({ name: 'ads-listing' });
          }
        });
      };

      const onSubmitUpdateEnglish = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
        submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        await store.dispatch(Actions.UPDATE_SEO_CONFIGURATION, {
          entityType: route.params.entityType,
          entityId: route.params.entityId,
          lang: 'en',
          data: values,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
        submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_SEO_CONFIGURATION'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          });
        }
      };

      const onSubmitUpdateArabic = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
        submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        await store.dispatch(Actions.UPDATE_SEO_CONFIGURATION, {
          entityType: route.params.entityType,
          entityId: route.params.entityId,
          lang: 'ar',
          data: values,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
        submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_SEO_CONFIGURATION'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          });
        }
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdateEnglish,
        onSubmitUpdateArabic,
        submitButton,
        translate,
        validationSchema,
        goBack,
        seoDataEn,
        seoDataAr,
        can,
        fetchSeoData,
        onSubmitBoth,
        seoNoFollowAr,
        seoNoIndexAr,
        seoNoFollowEn,
        seoNoIndexEn,
        seoSchemaEnJson,
        seoSchemaArJson,
      };
    },
  });
